import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { ArrowLeft } from '@phosphor-icons/react';

export default function ResetPassword() {
  const { t, i18n } = useTranslation();
  const { resetPasswordConfirm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  function toggleLanguage() {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  }

  const [email, setEmail] = useState((location.state as { email?: string })?.email || '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError(t('validation.passwordMismatch'));
      return;
    }

    setLoading(true);

    try {
      await resetPasswordConfirm(email, code, newPassword);
      navigate('/login');
    } catch {
      setError(t('error.generic'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="w-full py-6 bg-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <img src="/logo.png?v=2" alt="Arivo" className="h-[60px] w-auto" />
          <button type="button"
            onClick={toggleLanguage}
            className="px-3 py-1.5 text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 rounded-lg"
          >
            {i18n.language === 'en' ? 'FR' : 'EN'}
          </button>
        </div>
      </header>

      <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="text-center text-2xl font-bold text-navy-800">
            {t('auth.resetPassword')}
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 sm:rounded-xl sm:px-10 border border-slate-200">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="label">
                {t('auth.email')}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="input"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="code" className="label">
                {t('auth.verificationCode')}
              </label>
              <input
                id="code"
                name="code"
                type="text"
                required
                value={code}
                onChange={e => setCode(e.target.value)}
                className="input"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="newPassword" className="label">
                {t('auth.newPassword')}
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                required
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="input"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                {t('auth.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="input"
                disabled={loading}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-2.5"
              >
                {loading ? <LoadingSpinner size="sm" className="text-white" /> : t('auth.resetPassword')}
              </button>
            </div>

            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-primary-600 hover:text-primary-500"
              >
                <ArrowLeft className="w-4 h-4 mr-1" weight="light" />
                {t('auth.backToLogin')}
              </Link>
            </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
